import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Music, Plus, Edit2, Trash2, Upload, Loader2 } from 'lucide-react';

const EMPTY_TRACK = { title: '', description: '', audio_url: '', thumbnail_url: '' };

export const AdminPlaylistPage = () => {
  const { api, token } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TRACK);
  const [open, setOpen] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const audioInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const load = async () => {
    setLoading(true);
    try {
      const res = await api().get('/playlist');
      setTracks(res.data);
    } catch {
      toast.error('Failed to load playlist');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startNew = () => { setEditing(null); setForm(EMPTY_TRACK); setOpen(true); };
  const startEdit = (t) => {
    setEditing(t);
    setForm({ title: t.title || '', description: t.description || '', audio_url: t.audio_url || '', thumbnail_url: t.thumbnail_url || '' });
    setOpen(true);
  };

  const handleFileUpload = async (file, kind) => {
    if (!file) return;
    const setUploading = kind === 'audio' ? setAudioUploading : setThumbUploading;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const folder = kind === 'audio' ? 'playlist-audio' : 'playlist-thumbs';
      const res = await fetch(`${API}/upload?folder=${folder}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const url = `${API.replace(/\/api$/, '')}/api/files/${data.path}`;
      if (kind === 'audio') {
        setForm(prev => ({ ...prev, audio_url: url, title: prev.title || file.name.replace(/\.[^.]+$/, '') }));
      } else {
        setForm(prev => ({ ...prev, thumbnail_url: url }));
      }
      toast.success(`${kind === 'audio' ? 'Audio' : 'Thumbnail'} uploaded`);
    } catch (e) {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.audio_url) { toast.error('Upload or paste an audio URL'); return; }
    try {
      if (editing) await api().put(`/playlist/${editing.id}`, form);
      else await api().post('/playlist', form);
      toast.success(editing ? 'Track updated' : 'Track added');
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this track?')) return;
    try {
      await api().delete(`/playlist/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="admin-playlist-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold uppercase tracking-wide">Playlist</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage the homepage music playlist.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-track-btn" onClick={startNew} className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
              <Plus className="w-4 h-4 mr-2" /> Add Track
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">{editing ? 'Edit Track' : 'New Track'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Title *</Label>
                <Input data-testid="track-title-input" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Description</Label>
                <Textarea data-testid="track-description-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-zinc-800 border-zinc-700" rows={2} />
              </div>

              {/* Audio — upload or URL */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Audio *</Label>
                <div className="flex flex-col sm:flex-row gap-2 mt-1">
                  <input ref={audioInputRef} type="file" accept="audio/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0], 'audio')} data-testid="audio-file-input" />
                  <Button
                    type="button"
                    onClick={() => audioInputRef.current?.click()}
                    disabled={audioUploading}
                    data-testid="audio-upload-btn"
                    className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 flex-shrink-0"
                  >
                    {audioUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload from Device
                  </Button>
                  <Input value={form.audio_url} onChange={e => setForm({ ...form, audio_url: e.target.value })} placeholder="or paste URL" className="bg-zinc-800 border-zinc-700" data-testid="track-audio-url-input" />
                </div>
                {form.audio_url && (
                  <audio controls src={form.audio_url} className="mt-3 w-full h-10" style={{ filter: 'invert(0.9)' }} />
                )}
              </div>

              {/* Thumbnail — upload or URL */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Thumbnail</Label>
                <div className="flex flex-col sm:flex-row gap-2 mt-1">
                  <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0], 'thumb')} data-testid="thumb-file-input" />
                  <Button
                    type="button"
                    onClick={() => thumbInputRef.current?.click()}
                    disabled={thumbUploading}
                    data-testid="thumb-upload-btn"
                    className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 flex-shrink-0"
                  >
                    {thumbUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload Image
                  </Button>
                  <Input value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="or paste URL" className="bg-zinc-800 border-zinc-700" data-testid="track-thumb-url-input" />
                </div>
                {form.thumbnail_url && <img src={form.thumbnail_url} alt="" className="mt-3 w-20 h-20 rounded object-cover" />}
              </div>

              <Button type="submit" data-testid="save-track-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">
                {editing ? 'Update' : 'Add'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full" /></div>
      ) : tracks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800 rounded">
          <Music className="w-10 h-10 text-zinc-600 mb-3" />
          <p className="text-zinc-500">No tracks yet. Upload your first song.</p>
          <Button className="mt-4 bg-amber-500 hover:bg-amber-400 text-black font-bold" onClick={startNew}><Plus className="w-4 h-4 mr-2" /> Add Track</Button>
        </div>
      ) : (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
          {tracks.map((track, idx) => (
            <div key={track.id} data-testid={`admin-track-${idx}`} className="flex flex-col md:flex-row md:items-center gap-3 p-3 md:p-4 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900">
              <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                <div className="w-12 h-12 rounded bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {track.thumbnail_url ? <img src={track.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <Music className="w-5 h-5 text-black" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{track.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{track.description || 'No description'}</p>
                </div>
              </div>
              <audio controls src={track.audio_url} className="h-8 w-full md:w-64" style={{ filter: 'invert(0.9)' }} />
              <div className="flex items-center gap-2 self-end md:self-auto">
                <Button data-testid={`edit-track-${idx}`} size="sm" variant="outline" onClick={() => startEdit(track)} className="border-zinc-700">
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
                <Button data-testid={`delete-track-${idx}`} size="sm" variant="outline" onClick={() => handleDelete(track.id)} className="border-red-900 text-red-400 hover:bg-red-900/20">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
